import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { isMap, parseAllDocuments } from 'yaml'
import {
  applyGuardianAlphaConfig,
  applyGuardianAlphaInput,
  createGuardianAlphaNode,
  deleteGuardianAlphaNode,
  getGuardianAlphaService,
  getGuardianAlphaFileReadResult,
  readGuardianAlphaFile,
  saveGuardianAlphaConfig,
  saveGuardianAlphaInput,
  updateGuardianAlphaNode,
  upsertGuardianAlphaService,
} from '@/api/guardianAlpha'
import { useAuth } from '@/auth/useAuth'
import NodeHierarchyPicker from '@/components/NodeHierarchyPicker'
import GuardianAlphaNodeEditor from '@/components/guardian-alpha/GuardianAlphaNodeEditor'
import GuardianAlphaNodeSidebar from '@/components/guardian-alpha/GuardianAlphaNodeSidebar'
import type {
  GuardianAlphaEditorTab,
  GuardianAlphaMetadataDraft,
  GuardianAlphaYamlValidation,
} from '@/components/guardian-alpha/types'
import type {
  GuardianAlphaNode,
  GuardianAlphaNodeCreateRequest,
  GuardianAlphaService,
  NodeSummary,
} from '@/types'
import { manualOperatingTargetPath, nodeDetailPath } from '@/utils/navigation'
import styles from '@/components/guardian-alpha/guardianAlphaConfig.module.css'
import type { GuardianAlphaFileReadResult } from '@/api/guardianAlpha'


interface Props {
  nodes: NodeSummary[]
  nodesLoading: boolean
  selectedNodeId: string
  onSelectNode: (nodeId: string) => void
  selectedGuardianNodeId: string
  onSelectGuardianNode: (guardianNodeId: string) => void
  activeTab: GuardianAlphaEditorTab
  onSelectTab: (tab: GuardianAlphaEditorTab) => void
  manualOperatingEnabled?: boolean
}

const EMPTY_NODE_FORM: GuardianAlphaNodeCreateRequest = {
  logical_node_key: '',
  display_name: '',
  config_path: '',
  working_directory: '',
}

const EMPTY_METADATA_DRAFT: GuardianAlphaMetadataDraft = {
  logical_node_key: '',
  display_name: '',
  config_path: '',
  working_directory: '',
  process_entrypoint: '',
  expected_process_count: 1,
  manual_operating_profile: '',
  manual_operating_custom_actions: [],
}

export function validateGuardianAlphaYaml(
  yamlText: string,
): GuardianAlphaYamlValidation {
  if (new TextEncoder().encode(yamlText).byteLength > 1024 * 1024) {
    return { error: 'YAML은 1 MiB를 초과할 수 없습니다.', hasWeightPath: false }
  }
  const documents = parseAllDocuments(yamlText)
  const parseError = documents.flatMap((document) => document.errors)[0]
  if (parseError) {
    return { error: parseError.message, hasWeightPath: false }
  }
  if (documents.length !== 1 || !isMap(documents[0].contents)) {
    return {
      error: '단일 YAML document이며 root가 mapping이어야 합니다.',
      hasWeightPath: false,
    }
  }
  const parsed = documents[0].toJS() as Record<string, unknown>
  const weight = parsed.weight
  return {
    error: null,
    hasWeightPath: Boolean(
      weight
      && typeof weight === 'object'
      && !Array.isArray(weight)
      && Object.prototype.hasOwnProperty.call(weight, 'path'),
    ),
  }
}

export default function GuardianAlphaConfigPanel({
  nodes,
  nodesLoading,
  selectedNodeId,
  onSelectNode,
  selectedGuardianNodeId,
  onSelectGuardianNode,
  activeTab,
  onSelectTab,
  manualOperatingEnabled = false,
}: Props) {
  const { hasPermission } = useAuth()
  const canWriteConfig = hasPermission('guardian-alpha-config:write')
  const canViewNode = hasPermission('node:read')
  const canManageRegistration = hasPermission('node:metadata:write')
  const canWriteSettings = hasPermission('guardian-alpha-settings:write')
  const [service, setService] = useState<GuardianAlphaService | null>(null)
  const [serviceMissing, setServiceMissing] = useState(false)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [showCreate, setShowCreate] = useState(false)
  const [nodeForm, setNodeForm] = useState<GuardianAlphaNodeCreateRequest>(
    EMPTY_NODE_FORM,
  )
  const [yamlText, setYamlText] = useState('')
  const [inputYamlText, setInputYamlText] = useState('')
  const [inputSourceSha256, setInputSourceSha256] = useState<string | null>(null)
  const [inputReadCommandId, setInputReadCommandId] = useState<string | null>(null)
  const [readPreview, setReadPreview] = useState<GuardianAlphaFileReadResult | null>(null)
  const [readPreviewIntent, setReadPreviewIntent] = useState<'replace' | 'apply-input'>('replace')
  const readPreviewCloseButtonRef = useRef<HTMLButtonElement | null>(null)
  const selectedNodeIdRef = useRef(selectedNodeId)
  const selectedGuardianNodeIdRef = useRef('')
  const [metadataDraft, setMetadataDraft] = useState<GuardianAlphaMetadataDraft>(
    EMPTY_METADATA_DRAFT,
  )

  const loadService = useCallback(async (nodeId: string) => {
    if (!nodeId) {
      setService(null)
      setServiceMissing(false)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const loaded = await getGuardianAlphaService(nodeId)
      if (selectedNodeIdRef.current !== nodeId) return
      setService(loaded)
      setServiceMissing(false)
    } catch (caught: unknown) {
      if (selectedNodeIdRef.current !== nodeId) return
      const apiError = caught as { status?: number; message?: string }
      if (apiError.status === 404) {
        setService(null)
        setServiceMissing(true)
      } else {
        setError(apiError.message ?? 'GuardianAlpha 설정을 불러오지 못했습니다.')
      }
    } finally {
      if (selectedNodeIdRef.current === nodeId) setLoading(false)
    }
  }, [])

  selectedNodeIdRef.current = selectedNodeId

  useEffect(() => {
    loadService(selectedNodeId)
  }, [loadService, selectedNodeId])

  useEffect(() => {
    if (
      service?.node_id === selectedNodeId
      && selectedGuardianNodeId
      && !service.guardian_nodes.some((item) => item.id === selectedGuardianNodeId)
    ) {
      onSelectGuardianNode('')
    }
  }, [onSelectGuardianNode, selectedGuardianNodeId, selectedNodeId, service])

  const selectedGuardianNode = useMemo(
    () => (service?.node_id === selectedNodeId ? service : null)?.guardian_nodes.find(
      (item) => item.id === selectedGuardianNodeId,
    ) ?? null,
    [selectedGuardianNodeId, selectedNodeId, service],
  )

  useEffect(() => {
    if (!selectedGuardianNode) {
      setYamlText('')
      setInputYamlText('')
      setInputSourceSha256(null)
      setInputReadCommandId(null)
      setMetadataDraft(EMPTY_METADATA_DRAFT)
      return
    }
    setYamlText(selectedGuardianNode.yaml_text ?? '')
    setInputYamlText(selectedGuardianNode.input_yaml_text ?? '')
    setInputSourceSha256(selectedGuardianNode.input_source_sha256)
    // A fresh Read command is required before the next central save. The
    // persisted hash alone cannot prove which exact node snapshot was reviewed.
    setInputReadCommandId(null)
    setMetadataDraft({
      logical_node_key: selectedGuardianNode.logical_node_key,
      display_name: selectedGuardianNode.display_name,
      config_path: selectedGuardianNode.config_path,
      working_directory: selectedGuardianNode.working_directory,
      process_entrypoint: selectedGuardianNode.process_entrypoint,
      expected_process_count: selectedGuardianNode.expected_process_count,
      manual_operating_profile: selectedGuardianNode.manual_operating_profile ?? '',
      manual_operating_custom_actions: selectedGuardianNode.manual_operating_custom_actions
        .map((action) => ({ ...action })),
    })
  }, [selectedGuardianNode])

  useEffect(() => {
    selectedGuardianNodeIdRef.current = selectedGuardianNodeId
    setReadPreview(null)
    setReadPreviewIntent('replace')
  }, [selectedGuardianNodeId])

  useEffect(() => {
    if (!readPreview?.yaml_text) return undefined
    const previousBodyOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    readPreviewCloseButtonRef.current?.focus()
    return () => {
      document.body.style.overflow = previousBodyOverflow
    }
  }, [readPreview])

  const yamlValidation = useMemo(
    () => validateGuardianAlphaYaml(yamlText),
    [yamlText],
  )

  const dirtyTabs = useMemo<Record<GuardianAlphaEditorTab, boolean>>(() => {
    if (!selectedGuardianNode) {
      return { settings: false, config: false, input: false }
    }
    return {
      settings: (
        metadataDraft.logical_node_key !== selectedGuardianNode.logical_node_key
        || metadataDraft.display_name !== selectedGuardianNode.display_name
        || metadataDraft.config_path !== selectedGuardianNode.config_path
        || metadataDraft.working_directory !== selectedGuardianNode.working_directory
        || metadataDraft.process_entrypoint !== selectedGuardianNode.process_entrypoint
        || metadataDraft.expected_process_count !== selectedGuardianNode.expected_process_count
        || metadataDraft.manual_operating_profile
          !== (selectedGuardianNode.manual_operating_profile ?? '')
        || JSON.stringify(metadataDraft.manual_operating_custom_actions)
          !== JSON.stringify(selectedGuardianNode.manual_operating_custom_actions)
      ),
      config: yamlText !== (selectedGuardianNode.yaml_text ?? ''),
      input: (
        inputYamlText !== (selectedGuardianNode.input_yaml_text ?? '')
        || inputReadCommandId !== null
      ),
    }
  }, [
    inputReadCommandId,
    inputYamlText,
    metadataDraft,
    selectedGuardianNode,
    yamlText,
  ])
  const hasUnsavedChanges = Object.values(dirtyTabs).some(Boolean)

  useEffect(() => {
    if (!hasUnsavedChanges) return undefined
    const warnBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', warnBeforeUnload)
    return () => window.removeEventListener('beforeunload', warnBeforeUnload)
  }, [hasUnsavedChanges])

  function confirmDiscardChanges(): boolean {
    return !hasUnsavedChanges || window.confirm(
      '저장하지 않은 GuardianAlpha 변경사항이 있습니다. 변경사항을 버리고 다른 화면으로 이동할까요?',
    )
  }

  function selectPhysicalNode(nodeId: string) {
    if (nodeId === selectedNodeId || !confirmDiscardChanges()) return
    onSelectNode(nodeId)
    setMessage(null)
    setError(null)
    setReadPreview(null)
  }

  function selectGuardianNode(guardianNodeId: string) {
    if (guardianNodeId === selectedGuardianNodeId || !confirmDiscardChanges()) return
    onSelectGuardianNode(guardianNodeId)
    setMessage(null)
    setError(null)
    setReadPreview(null)
  }

  async function setupService() {
    if (!selectedNodeId) return
    setSaving(true)
    setError(null)
    try {
      const created = await upsertGuardianAlphaService(selectedNodeId, {
        restart_enabled: false,
      })
      setService(created)
      setServiceMissing(false)
      setMessage('GuardianAlpha 관리 대상을 등록했습니다. Restart는 기본 비활성화입니다.')
    } catch (caught: unknown) {
      setError((caught as { message?: string }).message ?? '등록에 실패했습니다.')
    } finally {
      setSaving(false)
    }
  }

  async function toggleRestartEnabled(enabled: boolean) {
    if (!selectedNodeId) return
    setSaving(true)
    setError(null)
    try {
      const updated = await upsertGuardianAlphaService(selectedNodeId, {
        restart_enabled: enabled,
      })
      setService(updated)
      setMessage(
        enabled
          ? 'Restart를 활성화했습니다. 최신 start.sh와 현장 preflight를 확인하세요.'
          : 'Restart를 비활성화했습니다.',
      )
    } catch (caught: unknown) {
      setError((caught as { message?: string }).message ?? '변경에 실패했습니다.')
    } finally {
      setSaving(false)
    }
  }

  async function createNode() {
    if (!selectedNodeId) return
    setSaving(true)
    setError(null)
    try {
      const created = await createGuardianAlphaNode(selectedNodeId, {
        ...nodeForm,
        display_name: nodeForm.display_name || undefined,
      })
      await loadService(selectedNodeId)
      onSelectGuardianNode(created.id)
      setNodeForm(EMPTY_NODE_FORM)
      setShowCreate(false)
      setMessage('GuardianNode를 등록했습니다.')
    } catch (caught: unknown) {
      setError((caught as { message?: string }).message ?? 'GuardianNode 등록에 실패했습니다.')
    } finally {
      setSaving(false)
    }
  }

  async function saveMetadata() {
    if (!selectedGuardianNode) return
    setSaving(true)
    setError(null)
    try {
      const updated = await updateGuardianAlphaNode(
        selectedGuardianNode.id,
        {
          ...metadataDraft,
          manual_operating_profile: metadataDraft.manual_operating_profile || null,
        },
      )
      setService((current) => current ? {
        ...current,
        guardian_nodes: current.guardian_nodes.map((item) => (
          item.id === updated.id ? updated : item
        )),
      } : current)
      setMessage('GuardianNode 실행 확인 정보를 저장했습니다.')
    } catch (caught: unknown) {
      setError((caught as { message?: string }).message ?? '메타데이터 저장에 실패했습니다.')
    } finally {
      setSaving(false)
    }
  }

  async function deleteNode(node: GuardianAlphaNode) {
    if (!window.confirm(`${node.display_name} GuardianNode 등록을 삭제할까요?`)) return
    setSaving(true)
    setError(null)
    try {
      await deleteGuardianAlphaNode(node.id)
      onSelectGuardianNode('')
      await loadService(selectedNodeId)
      setMessage('GuardianNode 등록을 삭제했습니다. 현장 파일은 삭제하지 않았습니다.')
    } catch (caught: unknown) {
      setError((caught as { message?: string }).message ?? '삭제에 실패했습니다.')
    } finally {
      setSaving(false)
    }
  }

  async function saveConfig(apply: boolean) {
    if (!selectedGuardianNode || yamlValidation.error) return
    setSaving(true)
    setError(null)
    try {
      await saveGuardianAlphaConfig(selectedGuardianNode.id, yamlText)
      if (apply) {
        await applyGuardianAlphaConfig(
          selectedGuardianNode.id,
          'GuardianAlpha config dashboard apply',
        )
        setMessage('config.yml Apply 명령을 생성했습니다. 적용 완료 후 GuardianAlpha Restart를 별도로 실행하세요.')
      } else {
        setMessage('YAML을 저장했습니다. 현장 적용 상태는 pending입니다.')
      }
      await loadService(selectedNodeId)
    } catch (caught: unknown) {
      setError(
        (caught as { message?: string }).message
        ?? (apply ? '저장 또는 Apply에 실패했습니다.' : '저장에 실패했습니다.'),
      )
      await loadService(selectedNodeId)
    } finally {
      setSaving(false)
    }
  }

  async function readFile(kind: 'config' | 'input') {
    if (!selectedGuardianNode) return
    const requestedGuardianNodeId = selectedGuardianNode.id
    setSaving(true)
    setError(null)
    setMessage(null)
    try {
      const command = await readGuardianAlphaFile(selectedGuardianNode.id, kind)
      setMessage(`${kind}.yml을 노드에서 읽는 중입니다.`)
      const readResult = await pollFileReadResult(
        selectedGuardianNode.id,
        kind,
        command.command_id,
      )
      if (requestedGuardianNodeId !== selectedGuardianNodeIdRef.current) return
      if (readResult.status !== 'succeeded' || !readResult.yaml_text) {
        throw new Error(readResult.error_message ?? `${kind}.yml Read에 실패했습니다.`)
      }
      setReadPreviewIntent('replace')
      setReadPreview(readResult)
      setMessage(`${kind}.yml을 노드에서 읽었습니다. 비교 팝업을 검토한 뒤 교체하세요.`)
    } catch (caught: unknown) {
      if (requestedGuardianNodeId !== selectedGuardianNodeIdRef.current) return
      setError((caught as { message?: string }).message ?? `${kind}.yml Read에 실패했습니다.`)
    } finally {
      setSaving(false)
    }
  }

  async function waitForInputApplyResult(
    physicalNodeId: string,
    guardianNodeId: string,
  ): Promise<GuardianAlphaNode | null> {
    for (let attempt = 0; attempt < 60; attempt += 1) {
      const loaded = await getGuardianAlphaService(physicalNodeId)
      if (
        physicalNodeId !== selectedNodeIdRef.current
        || guardianNodeId !== selectedGuardianNodeIdRef.current
      ) return null
      const current = loaded.guardian_nodes.find((item) => item.id === guardianNodeId)
      if (!current) throw new Error('적용 결과에서 GuardianNode를 찾을 수 없습니다.')
      if (current.input_last_apply_status !== 'applying') {
        setService(loaded)
        return current
      }
      await new Promise((resolve) => window.setTimeout(resolve, 1000))
    }
    throw new Error('input.yml Apply 결과 대기 시간이 초과했습니다. 잠시 후 상태를 다시 확인하세요.')
  }

  async function trackInputApply(
    physicalNodeId: string,
    guardianNodeId: string,
  ): Promise<'succeeded' | 'source-conflict' | 'cancelled'> {
    setMessage('input.yml Apply 명령을 전송했습니다. 노드의 적용 결과를 확인하는 중입니다.')
    const result = await waitForInputApplyResult(physicalNodeId, guardianNodeId)
    if (!result) return 'cancelled'
    if (result.input_last_apply_status === 'succeeded') {
      setMessage('input.yml 적용이 완료되었습니다. GuardianAlpha Restart를 별도로 실행하세요.')
      return 'succeeded'
    }

    const applyError = result.input_last_apply_error ?? 'input.yml Apply에 실패했습니다.'
    if (!isInputSourceConflict(applyError)) throw new Error(applyError)

    setError(applyError)
    setMessage('현장 input.yml이 Read 이후 변경되었습니다. 최신 snapshot을 읽어 비교하는 중입니다.')
    const command = await readGuardianAlphaFile(guardianNodeId, 'input')
    const readResult = await pollFileReadResult(
      guardianNodeId,
      'input',
      command.command_id,
    )
    if (
      physicalNodeId !== selectedNodeIdRef.current
      || guardianNodeId !== selectedGuardianNodeIdRef.current
    ) return 'cancelled'
    if (readResult.status !== 'succeeded' || typeof readResult.yaml_text !== 'string') {
      throw new Error(readResult.error_message ?? '충돌 후 최신 input.yml Read에 실패했습니다.')
    }
    setReadPreviewIntent('apply-input')
    setReadPreview(readResult)
    setMessage(
      '현장 변경으로 Apply가 중단되었습니다. 최신 snapshot과 현재 editor를 비교한 뒤 적용 방법을 선택하세요.',
    )
    return 'source-conflict'
  }

  async function saveInput(apply: boolean) {
    if (!selectedGuardianNode) return
    const validation = validateGuardianAlphaYaml(inputYamlText)
    if (validation.error) return
    const requestedGuardianNodeId = selectedGuardianNode.id
    setSaving(true)
    setError(null)
    setMessage(null)
    try {
      const persistedInputYamlText = selectedGuardianNode.input_yaml_text ?? ''
      if (
        apply
        && inputReadCommandId === null
        && inputYamlText === persistedInputYamlText
        && selectedGuardianNode.input_source_sha256
      ) {
        await applyGuardianAlphaInput(selectedGuardianNode.id)
        await trackInputApply(selectedNodeId, selectedGuardianNode.id)
        return
      }

      let sourceSha256 = inputSourceSha256
      let readCommandId = inputReadCommandId
      if (apply && (!sourceSha256 || !readCommandId)) {
        setMessage('적용 전 최신 input.yml을 노드에서 확인하는 중입니다.')
        const command = await readGuardianAlphaFile(selectedGuardianNode.id, 'input')
        const readResult = await pollFileReadResult(
          selectedGuardianNode.id,
          'input',
          command.command_id,
        )
        if (requestedGuardianNodeId !== selectedGuardianNodeIdRef.current) return
        if (
          readResult.status !== 'succeeded'
          || !readResult.source_sha256
          || typeof readResult.yaml_text !== 'string'
        ) {
          throw new Error(readResult.error_message ?? '적용 전 input.yml Read에 실패했습니다.')
        }
        if (readResult.yaml_text !== inputYamlText) {
          setReadPreviewIntent('apply-input')
          setReadPreview(readResult)
          setMessage(
            '현장 input.yml과 현재 editor가 다릅니다. 비교한 뒤 적용 여부를 명시적으로 선택하세요.',
          )
          return
        }
        sourceSha256 = readResult.source_sha256
        readCommandId = readResult.command_id
      }
      if (sourceSha256 && readCommandId) {
        await saveGuardianAlphaInput(
          selectedGuardianNode.id,
          inputYamlText,
          sourceSha256,
          readCommandId,
        )
      } else {
        await saveGuardianAlphaInput(selectedGuardianNode.id, inputYamlText)
      }
      if (apply) {
        await applyGuardianAlphaInput(selectedGuardianNode.id)
        await trackInputApply(selectedNodeId, selectedGuardianNode.id)
      } else {
        setMessage('input.yml draft를 저장했습니다. 현장 파일에는 아직 적용되지 않았습니다.')
        await loadService(selectedNodeId)
      }
    } catch (caught: unknown) {
      setMessage(null)
      setError((caught as { message?: string }).message ?? 'input.yml 저장 또는 Apply에 실패했습니다.')
    } finally {
      setSaving(false)
    }
  }

  function acceptReadPreview() {
    if (!readPreview?.yaml_text) return
    if (readPreview.kind === 'config') setYamlText(readPreview.yaml_text)
    else {
      setInputYamlText(readPreview.yaml_text)
      setInputSourceSha256(readPreview.source_sha256)
      setInputReadCommandId(readPreview.command_id)
    }
    setMessage(`${readPreview.kind}.yml editor를 노드 snapshot으로 교체했습니다. 아직 저장되지 않았습니다.`)
    setReadPreview(null)
    setReadPreviewIntent('replace')
  }

  function closeReadPreview() {
    if (!readPreview) return
    setMessage(`${readPreview.kind}.yml 노드 snapshot을 적용하지 않았습니다. 기존 editor 값을 유지합니다.`)
    setReadPreview(null)
    setReadPreviewIntent('replace')
  }

  async function applyInputFromReadPreview() {
    if (
      !selectedGuardianNode
      || readPreview?.kind !== 'input'
      || !readPreview.source_sha256
    ) return
    const validation = validateGuardianAlphaYaml(inputYamlText)
    if (validation.error) return
    const requestedGuardianNodeId = selectedGuardianNode.id
    const sourceSha256 = readPreview.source_sha256
    const readCommandId = readPreview.command_id
    setSaving(true)
    setError(null)
    setMessage(null)
    try {
      await saveGuardianAlphaInput(
        selectedGuardianNode.id,
        inputYamlText,
        sourceSha256,
        readCommandId,
      )
      if (requestedGuardianNodeId !== selectedGuardianNodeIdRef.current) return
      await applyGuardianAlphaInput(selectedGuardianNode.id)
      const applyResult = await trackInputApply(selectedNodeId, selectedGuardianNode.id)
      if (requestedGuardianNodeId !== selectedGuardianNodeIdRef.current) return
      if (applyResult === 'succeeded') {
        setReadPreview(null)
        setReadPreviewIntent('replace')
      }
    } catch (caught: unknown) {
      if (requestedGuardianNodeId !== selectedGuardianNodeIdRef.current) return
      setError((caught as { message?: string }).message ?? 'input.yml 저장 또는 Apply에 실패했습니다.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className={styles.panel}>
      <div className={styles.selectionGrid}>
        <section className={styles.card} aria-labelledby="physical-node-heading">
          <div className={styles.titleRow}>
            <div id="physical-node-heading" className={styles.cardTitle}>Physical Node</div>
            <div className={styles.headerActions}>
              {selectedNodeId && canViewNode && (
                <Link
                  to={nodeDetailPath(selectedNodeId)}
                  className={styles.linkButton}
                  onClick={(event) => {
                    if (!confirmDiscardChanges()) event.preventDefault()
                  }}
                >
                  Open node detail
                </Link>
              )}
              {service?.node_id === selectedNodeId && canManageRegistration && (
                <label className={styles.toggleLabel}>
                  <input
                    type="checkbox"
                    checked={service.restart_enabled}
                    disabled={saving}
                    onChange={(event) => toggleRestartEnabled(event.target.checked)}
                  />
                  Restart enabled
                </label>
              )}
            </div>
          </div>
          <p className={styles.description}>
            먼저 관리할 물리 노드를 선택하세요. 선택 전에는 설정 편집기를 표시하지 않습니다.
          </p>
          <NodeHierarchyPicker
            nodes={nodes}
            nodesLoading={nodesLoading}
            selectedNodeId={selectedNodeId}
            onSelectNode={selectPhysicalNode}
            ariaLabelPrefix="GuardianAlpha config"
          />
        </section>

        {!selectedNodeId ? (
          <section className={`${styles.card} ${styles.selectionCard}`}>
            <div className={styles.cardTitle}>GuardianNodes</div>
            <div className={styles.emptyState}>
              <strong>물리 노드를 선택하세요.</strong>
              <span>선택한 물리 노드에 등록된 논리 GuardianNode가 여기에 표시됩니다.</span>
            </div>
          </section>
        ) : loading ? (
          <section className={`${styles.card} ${styles.selectionCard}`}>
            <div className={styles.cardTitle}>GuardianNodes</div>
            <div className={styles.emptyState}>Loading...</div>
          </section>
        ) : serviceMissing ? (
          <section className={`${styles.card} ${styles.selectionCard}`}>
            <div className={styles.cardTitle}>GuardianNodes</div>
            <div className={styles.emptyState}>
              <span>이 물리 노드는 아직 GuardianAlpha 관리 대상으로 등록되지 않았습니다.</span>
              {canManageRegistration ? (
                <button
                  className={styles.primaryButton}
                  onClick={setupService}
                  disabled={saving}
                >
                  Register GuardianAlpha service
                </button>
              ) : (
                <span className={styles.warning}>
                  admin이 경로와 GuardianNode를 먼저 등록해야 합니다.
                </span>
              )}
            </div>
          </section>
        ) : service?.node_id === selectedNodeId ? (
          <GuardianAlphaNodeSidebar
            service={service}
            canManageRegistration={canManageRegistration}
            saving={saving}
            showCreate={showCreate}
            selectedGuardianNodeId={selectedGuardianNodeId}
            nodeForm={nodeForm}
            onToggleCreate={() => setShowCreate((current) => !current)}
            onNodeFormChange={setNodeForm}
            onCreateNode={createNode}
            onSelectNode={selectGuardianNode}
          />
        ) : null}
      </div>

      {(message || error) && (
        <div aria-live="polite">
          {message && <div className={styles.success}>{message}</div>}
          {error && <div className={styles.error}>{error}</div>}
        </div>
      )}

      {readPreview?.yaml_text && (
        <div
          className={styles.comparisonModalOverlay}
          data-testid="read-comparison-backdrop"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              event.preventDefault()
              readPreviewCloseButtonRef.current?.focus()
            }
          }}
        >
          <section
            className={styles.comparisonModal}
            role="dialog"
            aria-modal="true"
            aria-labelledby="read-comparison-title"
            onKeyDown={(event) => {
              if (event.key === 'Escape') {
                event.preventDefault()
                event.stopPropagation()
              }
            }}
          >
            <div className={styles.comparisonModalHeader}>
              <div>
                <div id="read-comparison-title" className={styles.cardTitle}>
                  {readPreview.kind}.yml Read comparison
                </div>
                <code className={styles.yamlPath}>
                  node hash {readPreview.source_sha256?.slice(0, 12) ?? '—'}
                </code>
              </div>
              <button
                ref={readPreviewCloseButtonRef}
                type="button"
                className={styles.comparisonModalClose}
                aria-label="Close comparison and keep current editor"
                title="Close and keep current editor"
                onClick={closeReadPreview}
              >
                ×
              </button>
            </div>
            <p className={styles.description}>
              현재 editor는 자동으로 덮어쓰지 않습니다. 노드 snapshot을 확인한 뒤 명시적으로 선택하세요.
              X로 닫으면 현재 editor 값을 유지하며 저장하거나 적용하지 않습니다.
            </p>
            <div className={styles.compareGrid}>
              <label className={styles.label}>
                <span>Current editor</span>
                <textarea
                  className={styles.compareTextarea}
                  readOnly
                  value={readPreview.kind === 'config'
                    ? yamlText
                    : inputYamlText}
                />
              </label>
              <label className={styles.label}>
                <span>Node snapshot</span>
                <textarea
                  className={styles.compareTextarea}
                  readOnly
                  value={readPreview.kind === 'config'
                    ? readPreview.yaml_text
                    : readPreview.yaml_text}
                />
              </label>
            </div>
            <div className={styles.comparisonModalActions}>
              <button
                type="button"
                className={styles.smallButton}
                onClick={acceptReadPreview}
                disabled={saving}
              >
                Replace editor with node snapshot
              </button>
              {readPreviewIntent === 'apply-input' && readPreview.kind === 'input' && (
                <button
                  type="button"
                  className={styles.primaryButton}
                  onClick={applyInputFromReadPreview}
                  disabled={saving}
                >
                  Apply current editor against this node snapshot
                </button>
              )}
            </div>
          </section>
        </div>
      )}

      {service?.node_id === selectedNodeId && !loading && (
        !selectedGuardianNode ? (
          <section className={styles.card}>
            <div className={styles.emptyState}>
              <strong>편집할 GuardianNode를 선택하세요.</strong>
              <span>
                물리 노드와 논리 GuardianNode를 모두 선택하면 Settings, config.yml,
                input.yml 탭이 활성화됩니다.
              </span>
            </div>
          </section>
        ) : (
          <GuardianAlphaNodeEditor
            node={selectedGuardianNode}
            activeTab={activeTab}
            onSelectTab={onSelectTab}
            dirtyTabs={dirtyTabs}
            canDeleteRegistration={canManageRegistration}
            canWriteSettings={canWriteSettings}
            canWriteConfig={canWriteConfig}
            saving={saving}
            metadataDraft={metadataDraft}
            yamlText={yamlText}
            inputYamlText={inputYamlText}
            yamlValidation={yamlValidation}
            onMetadataDraftChange={setMetadataDraft}
            onYamlTextChange={setYamlText}
            onInputYamlTextChange={setInputYamlText}
            onSaveMetadata={saveMetadata}
            onDeleteNode={deleteNode}
            onSaveConfig={saveConfig}
            onReadFile={readFile}
            onSaveInput={saveInput}
            manualOperatingPath={manualOperatingEnabled
              ? manualOperatingTargetPath(selectedGuardianNode.id)
              : null}
            onBeforeNavigate={confirmDiscardChanges}
          />
        )
      )}
    </div>
  )
}

async function pollFileReadResult(
  guardianNodeId: string,
  kind: 'config' | 'input',
  commandId: string,
) {
  for (let attempt = 0; attempt < 60; attempt += 1) {
    const result = await getGuardianAlphaFileReadResult(guardianNodeId, kind, commandId)
    if (!['queued', 'dispatched', 'accepted', 'running'].includes(result.status)) {
      return result
    }
    await new Promise((resolve) => window.setTimeout(resolve, 1000))
  }
  throw new Error(`${kind}.yml Read 결과 대기 시간이 초과했습니다.`)
}

function isInputSourceConflict(errorMessage: string): boolean {
  return errorMessage.includes('source_conflict')
    || errorMessage.includes('input.yml changed after it was read')
}
